<template>
  <div class="border border-amber-300 bg-amber-50/30 rounded-md">
    <div class="flex flex-wrap items-center gap-3 p-3 border-b border-amber-300 bg-amber-50">
      <span class="text-sm font-semibold text-amber-800">Bulk Edit Mode</span>
      <span class="text-xs text-amber-700">Only Draft rows are editable. Submitted rows are read-only.</span>
      <button
        class="ml-auto px-3 py-1.5 text-sm rounded border border-dark-primary text-dark-primary hover:bg-dark-primary hover:text-white"
        @click="addRow"
      >+ Add Row</button>
      <button
        class="px-3 py-1.5 text-sm rounded bg-accent-primary text-white hover:bg-accent-secondary disabled:opacity-50"
        :disabled="saving || dirtyOrNewCount === 0"
        @click="saveAll"
      >{{ saving ? 'Saving…' : `Save All (${dirtyOrNewCount})` }}</button>
      <button
        class="px-3 py-1.5 text-sm rounded border border-gray-400 hover:bg-gray-100"
        :disabled="saving"
        @click="cancel"
      >Cancel</button>
    </div>

    <div class="overflow-x-auto">
      <table class="table-fixed w-full min-w-[112rem] text-sm">
        <colgroup>
          <col class="w-12" />
          <col class="w-40" />
          <col class="w-32" />
          <col class="w-44" />
          <col />
          <col class="w-28" />
          <col class="w-32" />
          <col class="w-20" />
          <col class="w-48" />
          <col class="w-56" />
          <col class="w-28" />
          <col v-if="forOthers" class="w-44" />
          <col class="w-24" />
        </colgroup>
        <thead class="bg-dark-primary text-white">
          <tr>
            <th class="px-2 py-2"></th>
            <th class="text-left px-2 py-2">Status</th>
            <th class="text-left px-2 py-2">Date</th>
            <th class="text-left px-2 py-2">Category</th>
            <th class="text-left px-2 py-2">Description</th>
            <th class="text-right px-2 py-2">Amount</th>
            <th class="text-left px-2 py-2">Payment</th>
            <th class="text-center px-2 py-2">Reimb.</th>
            <th class="text-left px-2 py-2">Engagement</th>
            <th class="text-left px-2 py-2">Tags</th>
            <th class="text-center px-2 py-2">Receipts</th>
            <th v-if="forOthers" class="text-left px-2 py-2">For (submitter)</th>
            <th class="text-center px-2 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="rows.length === 0">
            <td :colspan="forOthers ? 13 : 12" class="px-3 py-4 text-center text-gray-500">
              No rows. Click "+ Add Row" to start a new draft.
            </td>
          </tr>
          <tr
            v-for="(row, idx) in rows"
            :key="row.uid"
            class="border-t border-gray-200 align-top"
            :class="{
              'bg-white': row.editable && !row.pendingDelete,
              'bg-gray-100 text-gray-500': !row.editable,
              'ring-2 ring-amber-300': (row.dirty || row.isNew) && !row.pendingDelete,
              'bg-red-50 line-through text-gray-400': row.pendingDelete,
            }"
          >
            <td class="px-2 py-2 text-xs text-gray-500">
              <span v-if="row.pendingDelete" class="px-1.5 py-0.5 rounded bg-red-100 text-red-700">delete</span>
              <span v-else-if="row.isNew" class="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">new</span>
              <span v-else-if="row.dirty" class="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">edited</span>
              <span v-else class="text-gray-400">#{{ row.id }}</span>
            </td>
            <td class="px-2 py-2">
              <StatusPill :status="row.values.status" />
            </td>

            <td class="px-2 py-2">
              <input
                v-if="row.editable"
                type="date"
                :value="row.values.date_of_expense"
                @input="updateField(row, 'date_of_expense', $event.target.value)"
                class="w-full h-8 px-1 border border-gray-300 rounded"
              />
              <span v-else>{{ formatDate(row.values.date_of_expense) }}</span>
            </td>

            <td class="px-2 py-2">
              <select
                v-if="row.editable"
                :value="row.values.category_id"
                @change="updateField(row, 'category_id', Number($event.target.value) || null)"
                class="w-full h-8 px-1 border border-gray-300 rounded"
              >
                <option :value="null">—</option>
                <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
              <span v-else>{{ row.values.category_name || '—' }}</span>
            </td>

            <td class="px-2 py-2">
              <input
                v-if="row.editable"
                type="text"
                :value="row.values.description"
                @input="updateField(row, 'description', $event.target.value)"
                class="w-full h-8 px-1 border border-gray-300 rounded"
              />
              <span v-else class="break-words">{{ row.values.description }}</span>
            </td>

            <td class="px-2 py-2 text-right">
              <input
                v-if="row.editable"
                type="number"
                step="0.01"
                min="0"
                :value="row.values.amount"
                @input="updateField(row, 'amount', $event.target.value)"
                class="w-full h-8 px-1 border border-gray-300 rounded text-right"
              />
              <span v-else>${{ formatAmount(row.values.amount) }}</span>
            </td>

            <td class="px-2 py-2">
              <select
                v-if="row.editable"
                :value="row.values.payment_method"
                @change="updateField(row, 'payment_method', $event.target.value)"
                class="w-full h-8 px-1 border border-gray-300 rounded"
              >
                <option value="personal_paid">Personal</option>
                <option value="company_paid">Company card</option>
              </select>
              <span v-else>{{ row.values.payment_method === 'company_paid' ? 'Company' : 'Personal' }}</span>
            </td>

            <td class="px-2 py-2 text-center">
              <input
                v-if="row.editable"
                type="checkbox"
                :checked="!!row.values.is_client_reimbursable"
                @change="updateField(row, 'is_client_reimbursable', $event.target.checked)"
              />
              <span v-else>{{ row.values.is_client_reimbursable ? 'Yes' : 'No' }}</span>
            </td>

            <td class="px-2 py-2">
              <select
                v-if="row.editable"
                :value="row.values.engagement_id"
                :disabled="!row.values.is_client_reimbursable"
                @change="updateField(row, 'engagement_id', Number($event.target.value) || null)"
                class="w-full h-8 px-1 border border-gray-300 rounded disabled:bg-gray-100"
              >
                <option :value="null">—</option>
                <option v-for="e in engagements" :key="e.id" :value="e.id">{{ engagementLabel(e) }}</option>
              </select>
              <span v-else>{{ row.values.engagement_label || '—' }}</span>
            </td>

            <td class="px-2 py-2">
              <TagPicker
                v-if="row.editable"
                :model-value="row.values.tags"
                :disabled="!row.editable"
                placeholder="Add tag…"
                @update:model-value="(v) => updateField(row, 'tags', v)"
              />
              <div v-else class="flex flex-wrap gap-1">
                <span v-for="t in row.values.tags" :key="t.id || t.name" class="px-1.5 py-0.5 text-xs rounded bg-accent-primary/70 text-white">{{ t.name }}</span>
              </div>
            </td>

            <td class="px-2 py-2 text-center">
              <div class="flex flex-col items-center gap-1">
                <span class="text-xs text-gray-600">
                  {{ (row.values.receipt_count || 0) + (row.queuedReceipts?.length || 0) }}
                </span>
                <button
                  v-if="row.editable"
                  @click="pickReceipt(idx)"
                  class="px-1.5 py-0.5 text-[10px] rounded border border-gray-400 hover:bg-gray-100"
                >Upload</button>
                <input
                  ref="receiptInputs"
                  type="file"
                  accept="image/*,application/pdf"
                  class="hidden"
                  @change="onReceiptChosen($event, idx)"
                />
              </div>
            </td>

            <td v-if="forOthers" class="px-2 py-2">
              <select
                v-if="row.isNew"
                :value="row.values.on_behalf_of_id"
                @change="updateField(row, 'on_behalf_of_id', Number($event.target.value) || null)"
                class="w-full h-8 px-1 border border-gray-300 rounded"
              >
                <option :value="null">— pick a person —</option>
                <option v-for="u in delegators" :key="u.id" :value="u.id">{{ delegatorLabel(u) }}</option>
              </select>
              <span v-else>{{ row.values.submitter_name || '—' }}</span>
            </td>

            <td class="px-2 py-2 text-center">
              <div class="flex items-center justify-center gap-1">
                <button
                  v-if="row.editable && !row.pendingDelete"
                  @click="duplicateRow(row)"
                  title="Duplicate row"
                  class="p-1 rounded border border-gray-400 hover:bg-gray-100"
                >
                  <DocumentDuplicateIcon class="h-4 w-4" />
                </button>
                <button
                  v-if="row.editable"
                  @click="removeRow(row)"
                  :title="row.pendingDelete ? 'Undo delete' : (row.isNew ? 'Remove unsaved row' : 'Delete draft')"
                  class="p-1 rounded border"
                  :class="row.pendingDelete
                    ? 'border-gray-400 text-gray-700 hover:bg-gray-100'
                    : 'border-red-400 text-red-600 hover:bg-red-50'"
                >
                  <ArrowUturnLeftIcon v-if="row.pendingDelete" class="h-4 w-4" />
                  <TrashIcon v-else class="h-4 w-4" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="errors.length > 0" class="m-3 p-2 rounded bg-red-50 border border-red-200 text-sm text-red-700">
      <div class="font-semibold mb-1">Some rows could not be saved:</div>
      <ul class="list-disc ml-5">
        <li v-for="(e, i) in errors" :key="i">Row {{ e.label }}: {{ e.detail }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default { name: 'my-expenses-bulk-edit-table' }
</script>

<script setup>
import { ref, computed, onMounted, defineProps, defineEmits } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toast-notification'
import {
  createExpense,
  updateExpense,
  deleteExpense,
  getExpensePickerEngagements,
} from '@/services/Expense.js'
import { getExpenseCategories } from '@/services/ExpenseCategory.js'
import { getMyDelegators } from '@/services/Delegation.js'
import { uploadExpenseReceipt } from '@/services/ExpenseReceipt.js'
import { addTagsToExpense, removeTagFromExpense, tagErrorMessage, isRetiredTagName } from '@/services/ExpenseTag.js'
import { DocumentDuplicateIcon, TrashIcon, ArrowUturnLeftIcon } from '@heroicons/vue/20/solid'
import TagPicker from './TagPicker.vue'
import StatusPill from './StatusPill.vue'

const props = defineProps({
  source: { type: Array, required: true },
  forOthers: { type: Boolean, default: false },
})
const emit = defineEmits(['cancel', 'saved'])

const store = useStore()
const $toast = useToast()

const categories = ref([])
const engagements = ref([])
const delegators = ref([])
const saving = ref(false)
const errors = ref([])
const receiptInputs = ref([])

let uidSeq = 1
function nextUid() { return `r${uidSeq++}` }

function snapshotFromRecord(r) {
  const engagementId = r.engagement_id
    ?? (r.engagement && typeof r.engagement === 'object' ? r.engagement.id : null)
    ?? (typeof r.engagement === 'number' ? r.engagement : null)
    ?? null
  return {
    date_of_expense: r.date_of_expense || null,
    category_id: r.category ? r.category.id : null,
    category_name: r.category ? r.category.name : null,
    description: r.description || '',
    amount: r.amount != null ? String(r.amount) : '',
    payment_method: r.payment_method || 'personal_paid',
    is_client_reimbursable: !!r.is_client_reimbursable,
    engagement_id: engagementId,
    engagement_label: r.engagement_title || (r.client_name ? `${r.client_name}` : null),
    tags: (r.tags || []).map((t) => ({ id: t.id, name: t.name })),
    receipt_count: r.receipt_count || 0,
    status: r.status,
    submitter_name: submitterName(r),
    on_behalf_of_id: null,
  }
}

function submitterName(r) {
  if (!r || !r.submitter) return ''
  const s = r.submitter
  return `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.username || ''
}

function makeRow(record) {
  const editable = record.status === 'draft'
  const original = snapshotFromRecord(record)
  return {
    uid: nextUid(),
    id: record.id,
    editable,
    isNew: false,
    dirty: false,
    pendingDelete: false,
    original,
    values: { ...original, tags: original.tags.map((t) => ({ ...t })) },
    originalTagIds: new Set((record.tags || []).map((t) => t.id).filter(Boolean)),
    queuedReceipts: [],
  }
}

function makeNewRow(seed = null) {
  const today = new Date()
  const yyyy = today.getFullYear()
  const mm = String(today.getMonth() + 1).padStart(2, '0')
  const dd = String(today.getDate()).padStart(2, '0')
  const blank = {
    date_of_expense: seed?.date_of_expense || `${yyyy}-${mm}-${dd}`,
    category_id: seed?.category_id ?? null,
    category_name: seed?.category_name ?? null,
    description: seed?.description || '',
    amount: seed?.amount || '',
    payment_method: seed?.payment_method || 'personal_paid',
    is_client_reimbursable: !!seed?.is_client_reimbursable,
    engagement_id: seed?.engagement_id ?? null,
    engagement_label: seed?.engagement_label ?? null,
    tags: (seed?.tags || []).map((t) => ({ ...t })),
    receipt_count: 0,
    status: 'draft',
    submitter_name: '',
    on_behalf_of_id: null,
  }
  return {
    uid: nextUid(),
    id: null,
    editable: true,
    isNew: true,
    dirty: true,
    pendingDelete: false,
    original: null,
    values: blank,
    originalTagIds: new Set(),
    queuedReceipts: [],
  }
}

const rows = ref(props.source.map(makeRow))

const dirtyOrNewCount = computed(() =>
  rows.value.filter((r) => r.editable && (r.isNew || r.dirty || r.pendingDelete)).length
)

function updateField(row, key, value) {
  if (!row.editable) return
  row.values[key] = value
  if (key === 'is_client_reimbursable' && !value) {
    row.values.engagement_id = null
  }
  if (key === 'category_id') {
    const cat = categories.value.find((c) => c.id === value)
    row.values.category_name = cat ? cat.name : null
  }
  if (key === 'engagement_id') {
    const eng = engagements.value.find((e) => e.id === value)
    row.values.engagement_label = eng ? engagementLabel(eng) : null
  }
  if (!row.isNew) {
    row.dirty = isRowChanged(row)
  }
}

function isRowChanged(row) {
  if (!row.original) return true
  const a = row.original
  const b = row.values
  if (a.date_of_expense !== b.date_of_expense) return true
  if (a.category_id !== b.category_id) return true
  if ((a.description || '') !== (b.description || '')) return true
  if (String(a.amount) !== String(b.amount)) return true
  if (a.payment_method !== b.payment_method) return true
  if (!!a.is_client_reimbursable !== !!b.is_client_reimbursable) return true
  if ((a.engagement_id || null) !== (b.engagement_id || null)) return true
  const aTags = new Set(a.tags.map((t) => t.id).filter(Boolean))
  const bTagIds = new Set(b.tags.map((t) => t.id).filter(Boolean))
  const bNewNames = b.tags.filter((t) => t.id == null).map((t) => t.name).filter(Boolean)
  if (bNewNames.length) return true
  if (aTags.size !== bTagIds.size) return true
  for (const id of aTags) if (!bTagIds.has(id)) return true
  return false
}

function addRow() {
  rows.value.push(makeNewRow())
}

function duplicateRow(row) {
  // Hand makeNewRow a plain snapshot rather than the live reactive
  // values object so it can't accidentally share references with the
  // source row.
  const seed = {
    date_of_expense: row.values.date_of_expense,
    category_id: row.values.category_id,
    category_name: row.values.category_name,
    description: row.values.description,
    amount: row.values.amount,
    payment_method: row.values.payment_method,
    is_client_reimbursable: row.values.is_client_reimbursable,
    engagement_id: row.values.engagement_id,
    engagement_label: row.values.engagement_label,
    tags: (row.values.tags || []).map((t) => ({ id: t.id ?? null, name: t.name })),
  }
  rows.value.push(makeNewRow(seed))
}

function removeRow(row) {
  if (row.isNew) {
    rows.value = rows.value.filter((r) => r.uid !== row.uid)
    return
  }
  if (!row.editable) return
  if (row.pendingDelete) {
    row.pendingDelete = false
    return
  }
  if (!window.confirm(`Delete draft expense #${row.id}? This will be applied when you click Save All.`)) return
  row.pendingDelete = true
}

function pickReceipt(idx) {
  const inputs = receiptInputs.value
  const el = Array.isArray(inputs) ? inputs[idx] : null
  el?.click()
}

function onReceiptChosen(event, idx) {
  const file = event.target.files?.[0]
  if (!file) return
  rows.value[idx].queuedReceipts.push(file)
  if (!rows.value[idx].isNew) rows.value[idx].dirty = true
  event.target.value = ''
}

function engagementLabel(e) {
  if (!e) return ''
  if (e.label) return e.label
  const client = e.client_name || (e.client && e.client.client_name) || ''
  const title = e.engagement_title || e.title || ''
  return [client, title].filter(Boolean).join(' — ') || `Engagement #${e.id}`
}

function delegatorLabel(u) {
  if (!u) return ''
  return u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username
}

function formatDate(iso) {
  if (!iso) return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(iso)
  return d.toLocaleDateString()
}

function formatAmount(v) {
  if (v === null || v === undefined || v === '') return '0.00'
  const n = typeof v === 'string' ? parseFloat(v) : v
  return Number.isFinite(n) ? n.toFixed(2) : '0.00'
}

function buildPayload(row) {
  const v = row.values
  const payload = {
    date_of_expense: v.date_of_expense,
    amount: v.amount,
    description: v.description,
    category_id: v.category_id,
    payment_method: v.payment_method,
    is_client_reimbursable: !!v.is_client_reimbursable,
    engagement_id: v.is_client_reimbursable ? v.engagement_id : null,
  }
  if (row.isNew && props.forOthers && v.on_behalf_of_id) {
    payload.on_behalf_of_id = v.on_behalf_of_id
  }
  return payload
}

function rowLabel(row, idx) {
  if (row.id) return `#${row.id}`
  return `new (${idx + 1})`
}

function validateRow(row) {
  const v = row.values
  if (!v.date_of_expense) return 'Date is required.'
  const amt = parseFloat(v.amount)
  if (!Number.isFinite(amt) || amt <= 0) return 'Amount must be > 0.'
  if (!v.category_id) return 'Category is required.'
  if (!v.description || !v.description.trim()) return 'Description is required.'
  if (v.is_client_reimbursable && !v.engagement_id) return 'Engagement is required for reimbursable expenses.'
  if (row.isNew && props.forOthers && !v.on_behalf_of_id) return 'Pick the person this expense is for.'
  if ((v.tags || []).some((t) => isRetiredTagName(t.name))) {
    return 'Retro tag is not allowed. Please use Is Retro checkbox.'
  }
  return null
}

async function syncTags(row, savedRecordId, token) {
  const currentIds = new Set(row.values.tags.map((t) => t.id).filter((x) => x != null))
  const toRemove = [...row.originalTagIds].filter((id) => !currentIds.has(id))
  const namesToAdd = row.values.tags
    .filter((t) => t.id == null || !row.originalTagIds.has(t.id))
    .map((t) => t.name)
    .filter(Boolean)
  if (namesToAdd.length) {
    try {
      await addTagsToExpense(savedRecordId, namesToAdd, token)
    } catch (error) {
      const msg = tagErrorMessage(error)
      if (!msg) throw error
      throw new Error(msg)
    }
  }
  for (const tagId of toRemove) {
    await removeTagFromExpense(savedRecordId, tagId, token)
  }
}

async function uploadQueuedReceipts(row, savedRecordId, token) {
  for (const file of row.queuedReceipts) {
    await uploadExpenseReceipt(token, savedRecordId, file)
  }
  row.queuedReceipts = []
}

async function saveAll() {
  const token = store.state.auth.token
  errors.value = []
  saving.value = true
  let savedCount = 0
  try {
    for (let i = 0; i < rows.value.length; i++) {
      const row = rows.value[i]
      if (!row.editable) continue
      if (row.pendingDelete) {
        if (!row.id) continue
        try {
          await deleteExpense(token, row.id)
          savedCount += 1
        } catch (err) {
          const detail = err?.response?.data?.detail || err?.message || 'Delete failed.'
          errors.value.push({ label: rowLabel(row, i), detail })
        }
        continue
      }
      if (!row.isNew && !row.dirty && row.queuedReceipts.length === 0) continue
      const validationError = validateRow(row)
      if (validationError) {
        errors.value.push({ label: rowLabel(row, i), detail: validationError })
        continue
      }
      try {
        const payload = buildPayload(row)
        let saved
        if (row.isNew) {
          saved = await createExpense(token, payload)
        } else {
          saved = await updateExpense(token, row.id, payload)
        }
        await syncTags(row, saved.id, token)
        await uploadQueuedReceipts(row, saved.id, token)
        savedCount += 1
      } catch (err) {
        const detail = err?.response?.data?.detail
          || (err?.response?.data && Object.values(err.response.data).flat().join(' '))
          || err?.message
          || 'Save failed.'
        errors.value.push({ label: rowLabel(row, i), detail })
      }
    }
    if (savedCount > 0) {
      $toast.open({
        message: `Saved ${savedCount} row${savedCount === 1 ? '' : 's'}${errors.value.length ? ' — see errors' : ''}`,
        type: errors.value.length ? 'warning' : 'success',
        duration: 3000,
      })
    } else if (errors.value.length === 0) {
      $toast.open({ message: 'No changes to save.', type: 'info', duration: 2000 })
    }
    if (errors.value.length === 0) {
      emit('saved')
    } else {
      emit('saved', { partial: true })
    }
  } finally {
    saving.value = false
  }
}

function cancel() {
  emit('cancel')
}

onMounted(async () => {
  const token = store.state.auth.token
  try {
    const cat = await getExpenseCategories(token)
    categories.value = Array.isArray(cat) ? cat : (cat.results || [])
  } catch (_) { categories.value = [] }
  try {
    const eng = await getExpensePickerEngagements(token)
    engagements.value = Array.isArray(eng) ? eng : (eng.results || [])
  } catch (_) { engagements.value = [] }
  if (props.forOthers) {
    try {
      const dels = await getMyDelegators(token)
      delegators.value = (dels || []).map((u) => ({
        ...u,
        full_name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username,
      }))
    } catch (_) { delegators.value = [] }
  }
})
</script>
